import { Edit, KeyboardArrowDown } from '@mui/icons-material';
import {
    Box,
    IconButton,
    InputBase,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FlexBox from 'components/flexbox/FlexBox';
import { Small, Tiny } from 'components/Typography';
import { format } from 'date-fns';
import { useMemo } from 'react';
import formatCurrency from 'utils/formatCurrency';

const CommonCell = ({ title, body }) => (
    <FlexBox flexDirection="column">
        <Small>{title}</Small>
        <Tiny color="text.secondary">{body}</Tiny>
    </FlexBox>
);

const columnShape = [
    {
        minWidth: 200,
        Header: 'Name',
        accessor: 'name',
        Cell: ({ row }) => {
            const { name } = row.original;
            return (
                <FlexBox alignItems="center">
                    <FlexBox flexDirection="column" ml={1.2}>
                        <Small mb={0.5}>{name}</Small>
                    </FlexBox>
                </FlexBox>
            );
        },
    },
    {
        minWidth: 100,
        Header: 'Capacity',
        accessor: 'capacity',
    },
    {
        Header: 'Workshop Type',
        accessor: 'workshopType.label',
        Cell: ({ row }) => {
            const { workshopType, price } = row.original;
            return (
                <CommonCell
                    title={workshopType.label}
                    body={formatCurrency(price)}
                />
            );
        },
    },
    {
        minWidth: 150,
        Header: 'Workshop Date',
        accessor: 'date',
        Cell: ({ row }) => {
            const { date } = row.original;
            return (
                <FlexBox alignItems="center">
                    <FlexBox flexDirection="column" ml={1.2}>
                        <Small mb={0.5}>{date}</Small>
                    </FlexBox>
                </FlexBox>
            );
        },
    },
    {
        Header: 'Action',
        accessor: 'id',
        Cell: ({ row, handleEdit }) => {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="Edit">
                        <IconButton
                            onClick={() => handleEdit(row.original)}
                            sx={{ color: 'text.disabled', fontSize: 18 }}
                        >
                            <Edit />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        },
    },
];

export function SelectColumnFilter({ column }) {
    const { filterValue, setFilter, preFilteredRows, id } = column;
    const theme = useTheme();
    const options = useMemo(() => {
        const options = new Set();
        preFilteredRows.forEach((row) => options.add(row.values[id]));
        return [...options.values()];
    }, [id, preFilteredRows]);

    return (
        <Select
            value={filterValue || ''}
            onChange={(e) => setFilter(e.target.value || undefined)}
            IconComponent={() => <KeyboardArrowDown color="disabled" />}
            input={
                <InputBase
                    sx={{
                        height: 40,
                        width: 110,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '0 8px',
                        borderRadius: '8px',
                        color: 'text.primary',
                        backgroundColor:
                            theme.palette.mode === 'light'
                                ? '#ECEFF5'
                                : 'divider',
                        '& .MuiPopover-paper': {
                            boxShadow: 'none',
                        },
                        '& > .MuiSelect-select': {
                            paddingRight: '0 !important',
                        },
                    }}
                />
            }
        >
            <MenuItem
                value=""
                sx={{
                    fontSize: 12,
                    fontWeight: 500,
                }}
            >
                All
            </MenuItem>
            {options.map((option, i) => (
                <MenuItem
                    key={i}
                    value={option}
                    sx={{
                        fontSize: 12,
                        fontWeight: 500,
                    }}
                >
                    {option}
                </MenuItem>
            ))}
        </Select>
    );
}

export function DateColumnFilter({ column }) {
    const { filterValue, setFilter } = column;
    const theme = useTheme();

    const handleChange = (newValue) => {
        const date = format(new Date(newValue), 'MMM dd, yyyy') || undefined;
        setFilter(date);
    };

    return (
        <DatePicker
            value={filterValue || ''}
            onChange={handleChange}
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        disabled
                        error={false}
                        inputProps={{
                            placeholder: '',
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                height: 40,
                                borderRadius: '8px',
                                backgroundColor:
                                    theme.palette.mode === 'light'
                                        ? '#ECEFF5'
                                        : 'divider', // width: 80,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: 0,
                                paddingLeft: 1,
                            },
                            '& .MuiSvgIcon-root': {
                                fontSize: 22,
                                color: 'text.secondary',
                            },
                        }}
                    />
                );
            }}
        />
    );
}
export default columnShape;
